import { initGuestLocale, setGuestLocale } from '../i18n';
import type { Locale } from '../i18n/types';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { LocaleContext, type LocaleContextValue } from './localeContextValue';

type LocaleProviderProps = {
  children: ReactNode;
};

export function LocaleProvider({ children }: LocaleProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => initGuestLocale());

  useEffect(() => {
    setGuestLocale(locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((nextLocale: Locale) => {
    // `t()` reads the active locale outside React. Update it before scheduling the
    // render so the render triggered by this state change sees the selected locale.
    setGuestLocale(nextLocale);
    document.documentElement.lang = nextLocale;
    setLocaleState(nextLocale);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
    }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
